import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { generateTOTPBarcode } from "../barcode";
import LFCTicket from "./LFC";
import GenericTicketCard from "./Generic";

const DigitalTicket = () => {
    const { ticketId } = useParams();
    const [ticketData, setTicketData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [localBarcodeImage, setLocalBarcodeImage] = useState(null);

    const generateLocalBarcode = useCallback(async () => {
        try {
            const cachedParams = JSON.parse(localStorage.getItem(`ticket_${ticketId}_params`));
            if (!cachedParams) throw new Error('No cached parameters');

            const barcodeImage = await generateTOTPBarcode(
                cachedParams.barcodeType,
                cachedParams.totpSecrets,
                cachedParams.intervalSize,
                cachedParams.pattern
            );

            setLocalBarcodeImage(barcodeImage);
            return true;
        } catch (err) {
            console.error('Local barcode error:', err);
            setError('Failed to generate barcode');
            return false;
        }
    }, [ticketId]);

    const fetchTicketData = useCallback(async () => {
        try {
            const res = await fetch(`https://api.tixly.app/v1/distribution/${ticketId}`);
            const data = await res.json();

            if (!data.ticket) {
                setError("Ticket not found");
                setTicketData(null);
            } else {
                // Update ticket data and cache parameters
                setTicketData(data.ticket);
                if (data.ticket.barcode) {
                    const params = {
                        barcodeType: data.ticket.barcode.type,
                        totpSecrets: data.ticket.barcode.totp_secrets,
                        intervalSize: data.ticket.barcode.refresh_interval,
                        pattern: data.ticket.barcode.pattern
                    };
                    localStorage.setItem(`ticket_${ticketId}_params`, JSON.stringify(params));

                    // Generate barcode immediately with new params
                    await generateLocalBarcode();
                }
                setError(null);
            }
        } catch (error) {
            if (!isOnline) {
                // Attempt offline generation
                const cachedTicket = localStorage.getItem(`ticket_${ticketId}`);
                if (cachedTicket) {
                    setTicketData(JSON.parse(cachedTicket));
                    const success = await generateLocalBarcode();
                    setError(success ? 'Using offline data' : 'Failed to generate barcode');
                } else {
                    setError('No cached data');
                }
            } else {
                setError("Error loading ticket");
            }
        } finally {
            setLoading(false);
        }
    }, [ticketId, isOnline, generateLocalBarcode]);

    // Network status effect
    useEffect(() => {
        const handleOnline = () => setIsOnline(true);
        const handleOffline = () => setIsOnline(false);

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    // Cache ticket data effect
    useEffect(() => {
        if (ticketData) {
            localStorage.setItem(`ticket_${ticketId}`, JSON.stringify(ticketData));
        }
    }, [ticketData, ticketId]);

    // Fetch and refresh effect
    useEffect(() => {
        let intervalId;

        const updateBarcode = async () => {
            if (!isOnline) {
                await generateLocalBarcode();
            } else {
                await fetchTicketData();
            }
        };

        // Initial fetch
        updateBarcode();

        // Set up refresh interval
        const refreshInterval = ticketData?.barcode?.refresh_interval || 30000; // default to 30s if not specified
        intervalId = setInterval(updateBarcode, refreshInterval);

        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [ticketId, isOnline, generateLocalBarcode, fetchTicketData]);



    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-100">
                <div className="animate-pulse text-lg">Loading ticket...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-100">
                <div className="w-full max-w-md bg-white rounded-xl shadow-lg p-6">
                    <div className="flex items-center mb-4">
                        <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                            <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                        </div>
                        <div className="ml-4">
                            <h3 className="text-lg font-medium text-gray-900">
                                Unable to load ticket
                            </h3>
                            {error === "Ticket not found" ? (
                                <div className="mt-2 text-sm text-gray-600">
                                    <p className="mb-2">
                                        We couldn't find the ticket you're looking for. This could be because:
                                    </p>
                                    <ul className="list-disc ml-6">
                                        <li>The ticket has expired</li>
                                        <li>The ticket ID is incorrect</li>
                                        <li>The ticket has been transferred or cancelled</li>
                                    </ul>
                                </div>
                            ) : (
                                <p className="mt-1 text-sm text-gray-600">
                                    {error === "Using offline data" ?
                                        "You're currently offline. Using cached ticket data." :
                                        "There was a problem loading your ticket. Please try again later."
                                    }
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-black p-4 flex items-center justify-center">
            {ticketData?.issuer_id === "3388000000014053627" ? (
                <LFCTicket data={ticketData} barcodeImage={localBarcodeImage} />
            ) : (
                <GenericTicketCard data={ticketData} barcodeImage={localBarcodeImage} />
            )}
        </div>
    );
};

export default DigitalTicket;
